"use client";

import { Search } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  IntentPrefetchLink,
  prefetchFullRoute,
} from "@/components/navigation/IntentPrefetchLink";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { GuideSearchEntry } from "./guide-presentation";

export type GuideNavigationCopy = {
  noSearchResults: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchResults: string;
  subtitle: string;
  title: string;
};

function normalizeQuery(value: string) {
  return value.trim().toLocaleLowerCase();
}

export function GuideNavigation({
  concepts,
  copy,
  overviewHref,
}: {
  concepts: GuideSearchEntry[];
  copy: GuideNavigationCopy;
  overviewHref?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();

  // A guide is a bounded set: warm every tab, including offscreen touch targets.
  // Stagger dispatch so the first paint and current route remain the priority.
  useEffect(() => {
    const timers = concepts.map((concept, index) =>
      window.setTimeout(
        () => prefetchFullRoute(router, concept.href),
        150 + index * 75,
      ),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [concepts, router]);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeQuery(deferredQuery);
  const results = useMemo(
    () =>
      normalizedQuery
        ? concepts.filter((concept) =>
            concept.searchText.includes(normalizedQuery),
          )
        : [],
    [concepts, normalizedQuery],
  );

  return (
    <div className="@container/guide-nav grid min-w-0 gap-5">
      <div className="grid gap-5 @4xl/guide-nav:grid-cols-[minmax(0,1fr)_minmax(20rem,32rem)] @4xl/guide-nav:items-end">
        <div>
          <h1 className="text-3xl">{copy.title}</h1>
          <p className="mt-1 text-muted-foreground">{copy.subtitle}</p>
        </div>

        <div className="relative">
          <label className="sr-only" htmlFor="guide-search">
            {copy.searchLabel}
          </label>
          <Search
            className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="guide-search"
            type="search"
            value={query}
            placeholder={copy.searchPlaceholder}
            className="h-12 rounded-full bg-card pr-4 pl-11"
            onChange={(event) => setQuery(event.target.value)}
          />

          {normalizedQuery ? (
            <div className="absolute top-[calc(100%+0.5rem)] right-0 left-0 z-30 overflow-hidden rounded-2xl border bg-popover shadow-lg">
              <p className="border-b px-4 py-3 font-semibold text-sm">
                {copy.searchResults}
              </p>
              {results.length > 0 ? (
                <div className="max-h-80 overflow-y-auto p-2">
                  {results.map((concept) => (
                    <IntentPrefetchLink
                      key={concept.id}
                      href={concept.href}
                      className="interaction-focus flex gap-3 rounded-xl px-3 py-3 hover:bg-accent focus-visible:bg-accent"
                      onClick={() => setQuery("")}
                    >
                      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 font-semibold text-primary text-xs">
                        {concept.number}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-semibold text-sm">
                          {concept.title}
                        </span>
                        {concept.subtitle ? (
                          <span className="mt-0.5 block line-clamp-2 text-muted-foreground text-xs">
                            {concept.subtitle}
                          </span>
                        ) : null}
                      </span>
                    </IntentPrefetchLink>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-5 text-muted-foreground text-sm">
                  {copy.noSearchResults}
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <nav
        aria-label={copy.title}
        className="relative flex snap-x snap-proximity scroll-px-2 gap-2 overflow-x-auto overscroll-x-contain rounded-2xl bg-muted/60 p-2 @2xl/guide-nav:grid @2xl/guide-nav:grid-cols-3 @2xl/guide-nav:overflow-visible @4xl/guide-nav:grid-cols-4"
      >
        {concepts.map((concept, index) => {
          const active =
            pathname === concept.href ||
            (index === 0 && pathname === overviewHref);

          return (
            <IntentPrefetchLink
              key={concept.id}
              href={concept.href}
              aria-current={active ? "page" : undefined}
              onFocus={(event) => {
                const link = event.currentTarget;
                const navigation = link.parentElement;
                if (
                  navigation &&
                  navigation.scrollWidth > navigation.clientWidth
                ) {
                  // Nearest snapping can leave the focused card partly clipped.
                  link.scrollIntoView({
                    block: "nearest",
                    inline: "start",
                    behavior: "instant",
                  });
                }
              }}
              ref={
                active
                  ? (link) => {
                      const navigation = link?.parentElement;
                      if (
                        !link ||
                        !navigation ||
                        navigation.scrollWidth <= navigation.clientWidth
                      )
                        return;
                      if (
                        link.offsetLeft < navigation.scrollLeft ||
                        link.offsetLeft + link.offsetWidth >
                          navigation.scrollLeft + navigation.clientWidth
                      ) {
                        navigation.scrollLeft =
                          link.offsetLeft -
                          (navigation.clientWidth - link.offsetWidth) / 2;
                      }
                    }
                  : undefined
              }
              className={cn(
                "interaction-focus flex w-[min(17rem,calc(100cqw-3rem))] min-w-0 shrink-0 snap-start items-center gap-2.5 rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-border hover:bg-card @2xl/guide-nav:w-auto",
                active && "interaction-selected",
              )}
            >
              <span
                className={cn(
                  "grid size-8 shrink-0 place-items-center rounded-full bg-card font-semibold text-muted-foreground text-xs",
                  active && "bg-primary text-primary-foreground",
                )}
              >
                {concept.number}
              </span>
              <span className="line-clamp-2 font-semibold text-sm leading-tight">
                {concept.title}
              </span>
            </IntentPrefetchLink>
          );
        })}
      </nav>
    </div>
  );
}
